import { useEffect, useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Button } from "../../../components";
import { CurrencyInput, DateInput, FormCheck, FormInput } from "../../../components/forms";
import RangeInput from "../../../components/forms/RangeInput";
import { AdviceTypesSelect, GenericAdviserSelect, MasterAccountSelect, ReportSelect, ServiceAgreementsSelect } from "../../../components/forms/selects";
import { useFilterContext } from "../../../hooks/FilterContext";
import ThemedModal from "../../../components/utilities/ThemedModal";

const NewBusinessRegisterFiltersModal = () => {
    const {
        filter,
        setFilter,
        showFilterModal,
        setShowFilterModal,
        defaultFilters
    } = useFilterContext();

    const onHide = () => setShowFilterModal(false);

    // TempFilters is state of the modal, filter from context is the applied filter to the query
    const [tempFilters, setTempFilters] = useState(filter);

    const updateFilter = (prop, value) => setTempFilters(old => ({
        ...old,
        [prop]: value
    }));

    // Clear resets temp filters to defaults
    const onClear = () => setTempFilters(defaultFilters);

    // Cancel resets temp filters to applied filter
    const onCancel = () => {
        setTempFilters(filter);
        onHide();
    }

    // Confirm updates query filter
    const onApply = () => setFilter(tempFilters);
    const onConfirm = () => {
        onApply();
        onHide();
    }

    const {
        startDate,
        endDate,
        masterAccountReference,
        applicationDateFrom,
        applicationDateTo,
        minInvestment,
        maxInvestment,
        minFee,
        maxFee,
        minNetFee,
        maxNetFee,
        masterAccountIds,
        adviserIds,
        adviceTypes,
        reportIds,
        executionOnly,
        serviceLevelAgreementIds
    } = tempFilters;

    useEffect(() => {
        setTempFilters(filter);
    }, [filter]);

    return <Modal
        show={showFilterModal}
        onHide={onCancel}
        size="lg"
        centered
    >
        <ThemedModal.Header closeButton>
            <Modal.Title>New Business Register Filters</Modal.Title>
        </ThemedModal.Header>
        <Modal.Body>
            <div className="row gy-2">
                {/* Row 1 */}
                <div className="col-4">
                    <FormInput
                        label="Reference"
                        value={masterAccountReference}
                        onBlur={(newValue) => updateFilter('masterAccountReference', newValue)}
                        disableAnimations
                    />
                </div>
                <div className="col-8">
                    <MasterAccountSelect
                        label="Client"
                        value={masterAccountIds}
                        setValue={(selections) => {
                            updateFilter('masterAccountIds', selections.map(selection => selection.value));
                            updateFilter('reportIds', [])
                        }}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>

                {/* Row 2 */}
                <div className="col-12">
                    <OverlayTrigger
                        placement="left"
                        overlay={(
                            masterAccountIds.length !== 1
                                ? <Tooltip>
                                    Please select a single master account to filter by Report
                                </Tooltip>
                                : <></>
                        )}
                    >
                        <span>
                            <ReportSelect
                                label="Report"
                                value={reportIds}
                                setValue={(selections) => updateFilter('reportIds', selections.map(selection => selection.value))}
                                masterAccountId={masterAccountIds.length === 1 ? masterAccountIds[0] : null}
                                isMulti
                                isClearable
                                isDisabled={masterAccountIds.length !== 1}
                            />
                        </span>
                    </OverlayTrigger>

                </div>

                {/* Row 3 */}
                <RangeInput
                    className="col-12"
                    InputComponent={DateInput}
                    label="Application Date"
                    startProps={{
                        value: applicationDateFrom,
                        onBlur: (date) => updateFilter('applicationDateFrom', date)
                    }}
                    endProps={{
                        value: applicationDateTo,
                        onBlur: (date) => updateFilter('applicationDateTo', date),
                        maxDate: new Date()
                    }}
                    disableAnimations
                    isClearable
                />

                {/* Row 4 */}
                <RangeInput
                    className="col-12"
                    InputComponent={DateInput}
                    label="Fee Date"
                    startProps={{
                        value: startDate,
                        onBlur: (date) => updateFilter('startDate', date)
                    }}
                    endProps={{
                        value: endDate,
                        onBlur: (date) => updateFilter('endDate', date)
                    }}
                    disableAnimations
                    isClearable
                />

                {/* Row 5 */}
                <RangeInput
                    className="col-12"
                    InputComponent={CurrencyInput}
                    label="Investment"
                    startProps={{
                        value: minInvestment ?? "",
                        onChange: (_, { floatValue }) => updateFilter('minInvestment', floatValue ?? null)
                    }}
                    endProps={{
                        value: maxInvestment ?? "",
                        onChange: (_, { floatValue }) => updateFilter('maxInvestment', floatValue ?? null)
                    }}
                    decimalScale={0}
                    disableAnimations
                />

                {/* Row 6 */}
                <RangeInput
                    className="col-6"
                    InputComponent={CurrencyInput}
                    label="Fee (Exc. VAT)"
                    startProps={{
                        value: minFee ?? "",
                        onChange: (_, { floatValue }) => updateFilter('minFee', floatValue ?? null)
                    }}
                    endProps={{
                        value: maxFee ?? "",
                        onChange: (_, { floatValue }) => updateFilter('maxFee', floatValue ?? null)
                    }}
                    decimalScale={0}
                    disableAnimations
                />
                <RangeInput
                    className="col-6"
                    InputComponent={CurrencyInput}
                    label="Fee (Inc. VAT)"
                    startProps={{
                        value: minNetFee ?? "",
                        onChange: (_, { floatValue }) => updateFilter('minNetFee', floatValue ?? null)
                    }}
                    endProps={{
                        value: maxNetFee ?? "",
                        onChange: (_, { floatValue }) => updateFilter('maxNetFee', floatValue ?? null)
                    }}
                    decimalScale={0}
                    disableAnimations
                />

                {/* Row 7 */}
                <div className="col-6">
                    <AdviceTypesSelect
                        label="Advice Type"
                        defaultValue={adviceTypes}
                        onBlur={(selections) => updateFilter('adviceTypes', selections.map(selection => selection.value))}
                        disableAnimations
                        isMulti
                        isClearable
                    />
                </div>
                <div className="col-6">
                    <GenericAdviserSelect
                        label="Adviser"
                        defaultValue={adviserIds}
                        onBlur={(selections) => updateFilter('adviserIds', selections.map(selection => selection.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>

                {/* Row 8 */}
                <div className="col-auto mt-auto mb-2">
                    <FormCheck
                        label="Execution Only"
                        isChecked={executionOnly}
                        onChange={(isChecked) => updateFilter('executionOnly', isChecked)}
                        disableAnimations
                    />
                </div>
                <div className="col">
                    <ServiceAgreementsSelect
                        label="Service Level"
                        defaultValue={serviceLevelAgreementIds}
                        includeNoneOption
                        onChange={(selections) => updateFilter('serviceLevelAgreementIds', selections.map(selection => selection.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="warning" onClick={onClear}>
                Clear Filters
            </Button>
            <Button variant="success" onClick={onConfirm}>
                Apply Filters
            </Button>
            <Button variant="light" onClick={onCancel}>
                Cancel
            </Button>
        </Modal.Footer>
    </Modal>
}

export default NewBusinessRegisterFiltersModal;